import React, { useRef } from 'react';
import { UploadCloud, CheckCircle, AlertCircle, RefreshCw, X } from 'lucide-react';
import './AdminJsonUploadMini.css';

const AdminJsonUploadMini = ({ 
  onUpload, 
  isUploading = false, 
  disabled = false,
  uploadStatus = 'idle', // 'idle' | 'success' | 'error'
  onResetStatus
}) => {
  const fileInputRef = useRef(null);

  const processFile = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile || disabled || isUploading) return;

    // Optional frontend verification safeguard
    if (selectedFile.type !== 'application/json' && !selectedFile.name.endsWith('.json')) {
      if (onUpload) onUpload(null, new Error('Invalid file format. Please upload a .json file.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsedJson = JSON.parse(event.target.result);
        if (onUpload) {
          // Passes both the parsed JSON schema payload and raw file handle up to parent container hooks
          onUpload(parsedJson, null);
        }
      } catch (error) {
        if (onUpload) onUpload(null, new Error('Failed to parse JSON syntax structure.'));
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleClearClick = (e) => {
    e.stopPropagation();
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (onResetStatus) onResetStatus();
  };

  // Determine standard active rendering configuration state mapping rules
  let componentState = 'idle';
  if (disabled) componentState = 'disabled';
  else if (isUploading) componentState = 'parsing';
  else if (uploadStatus === 'success') componentState = 'success';
  else if (uploadStatus === 'error') componentState = 'error';

  return (
    <div className={`upload-mini-badge status-${componentState}`}>
      <input 
        type="file" 
        ref={fileInputRef} 
        onChange={processFile} 
        accept=".json" 
        disabled={disabled || isUploading}
        className="hidden-file-input" 
      />

      {componentState === 'idle' && (
        <button 
          className="btn-upload-trigger" 
          onClick={() => fileInputRef.current.click()}
          disabled={disabled}
        >
          <UploadCloud size={16} />
          <span>Upload JSON</span>
        </button>
      )}

      {componentState === 'disabled' && (
        <button className="btn-upload-trigger" disabled title="Pre-conditions not met">
          <UploadCloud size={16} />
          <span>Upload JSON</span>
        </button>
      )}

      {componentState === 'parsing' && (
        <div className="upload-mini-state">
          <RefreshCw size={14} className="icon-spin" />
          <span>Uploading to server...</span>
        </div>
      )}

      {componentState === 'success' && (
        <div className="upload-mini-state">
          <CheckCircle size={14} className="icon-success" />
          <span>Saved Successfully!</span>
          <button className="btn-mini-clear" onClick={handleClearClick} title="Reset upload form">
            <X size={14} />
          </button>
        </div>
      )}

      {componentState === 'error' && (
        <div className="upload-mini-state" onClick={() => fileInputRef.current.click()}>
          <AlertCircle size={14} className="icon-danger" />
          <span>API Error. Try Again</span>
          <button className="btn-mini-clear" onClick={handleClearClick}>
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminJsonUploadMini;